import {TTemplatePreviewDto} from '@shared/components/issues/types/template-preview-dto.type';
import { Injectable, inject } from "@angular/core";
import {Actions} from "@ngrx/effects";
import {select, Store} from "@ngrx/store";
import {exhaustMap, finalize, mergeMap, Observable, withLatestFrom} from "rxjs";
import {catchError, map, tap} from "rxjs/operators";
import {ILocalPageOutputDto} from "@shared/interfaces/orga-response.interface";
import {
  FileDto,
  FilterDto,
  FilterOperators,
  IssueContentType,
  IssueListDto,
  IssueTemplateListDto,
  IssueType,
  Orders
} from "@server-models";
import {Router} from "@angular/router";
import {SharedImageService} from "@shared/services/shared-image.service";
import {SharedCameraService} from "@shared/services/shared-camera.service";
import {SharedModalLoaderService} from "@shared/services/shared-modal-loader.service";
import {TranslateService} from "@ngx-translate/core";
import {SharedIssueBaseService} from "@shared/components/issues/services/shared-issue-base.service";
import {SharedIssueApiBaseEffects} from "@shared/components/issues/store/shared-issue-api-base.effects";
import {SharedIssueBaseActions} from "@shared/components/issues/store/shared-issue-base.action-type";
import {TIssuePreview} from "@shared/components/issues/store/shared-issue-base.actions";
import {SharedIssueService} from "@shared/components/issues/services/shared-issue.service";
import {FormGroup} from "@angular/forms";
import {IIssueService} from "@shared/components/issues/interfaces/issue-service.interface";
import {TSubmitIssueDto} from "@shared/components/issues/types/submit-issue-dto.type";
import {TechIssueService} from "@tech/pages/issues/services/tech-issue.service";
import {TechIssuesActions} from "@tech/pages/issues/store/tech-issue.actions-type";
import {TechIssuesSelectors} from "@tech/pages/issues/store/tech-issue.selector-type";
import {SharedCacheService} from "@shared/services/cache/shared-cache.service";
import {SharedFileApiService} from "@shared/services/file/shared-file-api.service";
import {ILocalIssueDetailData} from "@shared/components/issues/interfaces/local-issue-detail-data.interface";
import {SharedIssueApiService} from "@shared/components/issues/services/shared-issue-api.service";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";

@Injectable({
  providedIn: 'root'
})
export class TechIssueApiEffects extends SharedIssueApiBaseEffects {
  private _techIssuesService = inject(TechIssueService);
  private _sharedFileApiService = inject(SharedFileApiService);


  constructor() {
    const actions$ = inject(Actions);
    const store = inject(Store);
    const _cameraService = inject(SharedCameraService);
    const _sharedModalLoaderService = inject(SharedModalLoaderService);
    const _translateService = inject(TranslateService);
    const _imageService = inject(SharedImageService);
    const _issuesBaseService = inject(SharedIssueBaseService);
    const _sharedIssueApiService = inject(SharedIssueApiService);
    const _issueService = inject(SharedIssueService);
    const _cacheService = inject(SharedCacheService);
    const _router = inject(Router);

    super(
      actions$,
      store,
      _cameraService,
      _sharedModalLoaderService,
      _translateService,
      _imageService,
      _issuesBaseService,
      _sharedIssueApiService,
      _issueService,
      _cacheService,
      _router
    );
  }

  override requestGetPaginated(action: ISharedLocalBodyRequest): Observable<(ILocalPageOutputDto<IssueListDto[]> | any)> {
    const body: FilterDto[] = [
      ...action.filters,
      {
        property: "issueType",
        value: IssueType.WinterhalterServiceCall.toString(),
        operator: FilterOperators.NotEqual
      }
    ];

    const options = {
      params: {
        PageSize: action.pagination.pageSize.toString(),
        PageNumber: action.pagination.pageNumber.toString(),
        Cols: action.pagination.cols,
        SortField: "lastModified",
        Sort: Orders.Descending.toString()
      }
    }
    return this._sharedIssueApiService.getAllIssueList(body, options).pipe(
      map((data: ILocalPageOutputDto<IssueListDto[]>) => {
          return action.isRefresh
            ? TechIssuesActions.getIssueListPaginatedRefresh({ data })
            : TechIssuesActions.getIssueListPaginatedSuccess({ data })
        }
      ),
      catchError((error) => [TechIssuesActions.getIssueListPaginatedFail({ error })])
    )
  }

  override requestGetTemplate(templateId?: number,
                              resourceId?: number,
                              filters: FilterDto[] = [],
                              pageNumber?: number): Observable<(ILocalPageOutputDto<IssueTemplateListDto[]> | any)> {
    const body = [
      ...filters
    ];

    if (resourceId) {
      return this._sharedIssueApiService.getIssueTemplateListByResource(resourceId, body, pageNumber).pipe(
        map((data: ILocalPageOutputDto<IssueTemplateListDto[]>) =>
          TechIssuesActions.getIssueTemplateListSuccess({ data, templateId, resourceId })
        ),
        catchError((error) => [TechIssuesActions.getIssueTemplateListFail({ error })])
      );
    } else {
      return this._sharedIssueApiService.getIssueTemplateList(body, pageNumber).pipe(
        map((data: ILocalPageOutputDto<IssueTemplateListDto[]>) =>
          TechIssuesActions.getIssueTemplateListSuccess({ data, templateId })
        ),
        catchError((error) => [TechIssuesActions.getIssueTemplateListFail({ error })])
      );
    }
  }

  override formatReadDetailDataFinish(action$: Observable<{
    detailDataFormatted: ILocalIssueDetailData
  }>): Observable<any> {
    return action$;
  }

  override requestByIdByIssueType(action: { issueId: number, issueType: IssueType }): Observable<any> {
    const apiService: IIssueService = this._techIssuesService.getApiServiceByIssueType(action.issueType);
    return apiService.getIssue(action.issueId).pipe(
      map((data) =>
        TechIssuesActions.getByIdSuccess({ data, cacheGuid: this._cacheService.generateGuid() })
      ),
      catchError((error) => [TechIssuesActions.getByIdFail({ error })])
    );
  }

  override saveSignatureRequest(tenantId: number, blob: Blob, dataUrl: string, fileType: string,
                                index: number | string): Observable<{
    signature: FileDto
  } | any> {
    return this._sharedFileApiService.upload(tenantId, blob, fileType).pipe(
      exhaustMap((data: FileDto) => {
          return [TechIssuesActions.saveSignatureSuccess({
            id: data.fileId!,
            dataUrl,
            index
          })]
        }
      ),
      catchError((error) => [TechIssuesActions.saveSignatureFail({ error })])
    )
  }

  override savePictureRequest(tenantId: number, blob: Blob, fileType: string, pictureId: number): Observable<{
    image: FileDto
  } | any> {
    return this._sharedFileApiService.upload(tenantId, blob, fileType).pipe(
      exhaustMap((data: FileDto) => {
          return [TechIssuesActions.savePictureSuccess({ fileId: data.fileId!, path: data.path!, pictureId })]
        }
      ),
      catchError((error) => [TechIssuesActions.savePictureFail({ error })])
    )
  }

  override requestIssuePreviewByContentType(action: TIssuePreview): Observable<(unknown | any)> {
    const apiService: IIssueService = this._techIssuesService.getApiServiceByIssueContentType(action.issueType);
    const preview: Observable<TTemplatePreviewDto> = action.issueType === IssueContentType.Connected
      ? this._techIssuesService.previewConnected(action.templateId, action.resourceId)
      : apiService.getIssuePreview(action.templateId);
    return preview.pipe(
      map((data) =>
        TechIssuesActions.getIssuePreviewSuccess({
          data: data,
          templateId: action.templateId,
          resourceId: action.resourceId,
          contentType: action.issueType
        })
      ),
      catchError((error) => [TechIssuesActions.getIssuePreviewFail({ error })])
    )
  }

  override prepareFormsToSendTemplatePreview(action$: Observable<{
    form: FormGroup,
    templatePreviewDto: any
  }>): Observable<any> {
    return action$.pipe(
      withLatestFrom(this.store.pipe((select(TechIssuesSelectors.selectCurrentPreviewIssueType)))),
      mergeMap(([{ form, templatePreviewDto }, templateIssueType]) => {
        const service = this._issueService.getIssueFormService(templateIssueType);
        const submitReady = service.prepareFormIssueDto(form, templatePreviewDto);
        return [TechIssuesActions.prepareFormsToSendTemplatePreviewDone({ submitReady })]
      })
    );
  }

  override sendTemplatePreview(action$: Observable<{
    issuePrepared: TSubmitIssueDto
  }>): Observable<void> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(TechIssuesSelectors.selectCurrentPreviewId))),
      withLatestFrom(this.store.pipe((select(TechIssuesSelectors.selectCurrentPreviewIssueType)))),
      mergeMap(([[{ issuePrepared }, templateId], issueContentType]) => {
        return this.sendTemplatePreviewByIssueType({
          templateId,
          issuePrepared,
          issueContentType
        });
      })
    );
  }

  override sendTemplatePreviewByIssueType(action: {
    templateId: number,
    issuePrepared: TSubmitIssueDto
    issueContentType: IssueContentType
  }): Observable<(unknown | any)> {
    const apiService = this._techIssuesService.getApiServiceByIssueContentType(action.issueContentType);
    return apiService.sendIssueTemplatePreviewById(action.templateId, action.issuePrepared).pipe(
      map(() => TechIssuesActions.sendTemplatePreviewSuccess({})),
      catchError(error => [TechIssuesActions.sendTemplatePreviewFail({ error })]),
      finalize(() => {
        this._sharedModalLoaderService.dismiss();
      })
    );
  }
}
